import CategorySeo from '@common/components/CategorySeo';
import ProductCardLoading from '@common/components/ProductCard/ProductCardLoading';
import { ReactComponent as BreadcrumbRightArrow } from '@common/icons/BreadcrumbRightArrow.svg';
import { RootState } from '@common/store';
import { addProductToCart, subtractProductFromCart } from '@common/store/cart/slice';
import { fetchProductsRequest, resetProductsStore } from '@common/store/products/slice';
import { addToast } from '@common/store/toasts/slice';
import { PRODUCTS_KEY } from '@common/utils/constants';
import CategorySidebar from '@components/CategorySidebar';
import PageLayout from '@components/PageLayout';
import Pagination from '@components/Pagination';
import ProductCard from '@components/ProductCard';
import Arrow from '@images/Arrow.svg';
import GreyBgArrow from '@images/BlackArrow.svg';
import CartImg from '@images/Empty.svg';
import Error from '@lib/Error';
import Image from '@lib/Image';
import InjectScript from '@lib/InjectScript';
import Link from '@lib/Link';
import Loading from '@lib/Loading';
import MDPInjectScript from '@lib/MDPInjectScript';
// import Modal from '@lib/Modal';
import classnames from 'classnames';
import React, { useContext, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useDispatch, useSelector } from 'react-redux';

import { CategoryContext, CategoryProvider } from '../../../../common/context/CategoryContext';
import { MDPPageType } from '../../../../common/models/MDPAppScript';
import { PRODUCT_REFERRED_FROM } from '../../../../common/models/Product';
import AttributesModal from './AttributesModal';

const Category = () => {
  const {
    categoryId,
    category,
    categoryProductsSlice,
    computedSortOptions: { SELECTED_SORT_OPTION, UNSELECTED_SORT_OPTIONS },
  } = useContext(CategoryContext);

  const cart = useSelector(({ cart }: RootState) => cart);
  const storeInfo = useSelector(({ storeInfo }: RootState) => storeInfo);
  const dispatch = useDispatch();
  const [showAttributesModal, setShowAttributesModal] = useState(false);
  const [isSortDropdownOpen, setIsSortDropdownOpen] = useState(false);

  return (
    <PageLayout hideToast>
      <InjectScript others="category" category={categoryId} />
      <MDPInjectScript pageType={MDPPageType.CATEGORY_PAGE} />
      <MDPInjectScript pageType={MDPPageType.STORE_PAGE} />
      <div className="container md:mx-auto my-16 md:my-20 py-14 cursor-pointer">
        <div className="flex items-center text-12 md:text-14 font-medium" font-customisation="para-text">
          <Link to="/" className="mr-12 text-gray " color-customisation="page-body-text">
            Home
          </Link>
          <BreadcrumbRightArrow className="mr-14" />
          <div className="text-theme font-medium">
            <span color-customisation="page-body-text">{category?.category?.name}</span>
          </div>
        </div>
      </div>
      <div className="container flex md:mx-auto">
        <CategorySeo categoryName={category?.category?.name} />
        {category?.isFetched && !category?.error ? (
          <Helmet>
            <title>{category?.category?.pageTitle || category?.category?.name}</title>
            {category?.category?.description && <meta name="description" content={category?.category?.description} />}
            {category?.category?.description && (
              <meta property="og:description" content={category?.category?.description} />
            )}
            {category?.category?.image && (
              <meta itemProp="image" property="og:image" content={category?.category?.image} />
            )}
          </Helmet>
        ) : null}

        <div className="hidden md:block md:w-228 ">
          {!category.isFetched ? (
            <Loading />
          ) : category.isFetched && category.error ? (
            <Error message={category.error} />
          ) : !showAttributesModal ? (
            <CategorySidebar category={category.category} />
          ) : null}
        </div>
        <div className="relative w-full md:ml-32 md:w-5/6 ">
          <div className="flex flex-col w-full">
            {showAttributesModal ? <AttributesModal closeModal={() => setShowAttributesModal(false)} /> : null}
            {categoryProductsSlice?.list?.count ? (
              <div className={classnames('md:flex justify-between items-center')}>
                <div className="flex md:ml-16 justify-center md:justify-left">
                  {!categoryProductsSlice?.isFetching && (
                    <h1
                      className="text-gray leading-24 text-14 md:text-16 font-semibold"
                      font-customisation="section-heading"
                      color-customisation="page-heading"
                    >
                      {category?.category?.name}
                    </h1>
                  )}
                </div>

                <div className="flex justify-between items-center mt-20 md:mt-0">
                  <div
                    className="flex flex-col h-20 sm:flex md:hidden text-14 font-bold tracking-14 uppercase cursor-pointer"
                    onClick={() => {
                      setShowAttributesModal(true);
                    }}
                    font-customisation="section-heading"
                    color-customisation="page-heading"
                  >
                    <div>Filter</div>
                    <div className="border-2 rounded opacity-75 border-theme"></div>
                  </div>
                  <div className="items-center cursor-pointer">
                    <div
                      className="text-16 text-theme leading-20 flex justify-between pl-10 w-240 h-46 border-2 items-center border-black rounded-md flex-wrap focus:outline-none focus:h-200 hover:bg-disabled font-semibold bg-white"
                      onClick={() => {
                        setIsSortDropdownOpen(!isSortDropdownOpen);
                      }}
                      font-customisation="para-text"
                      color-customisation="page-body-text"
                    >
                      <span>{SELECTED_SORT_OPTION?.name}</span>
                      <div className="cursor-pointer w-30 h-30">
                        <Image
                          className={'object-cover rounded w-full h-full transform rotate-90 bg-white'}
                          src={isSortDropdownOpen ? GreyBgArrow : Arrow}
                          noDefaultDimensions
                          alt="Down"
                        />
                      </div>
                    </div>
                    <div className="mt-2">
                      {isSortDropdownOpen && (
                        <div
                          className="flex absolute flex-col w-240 border-2 border-black bg-white z-40 rounded "
                          onMouseLeave={(e) => {
                            e.preventDefault();
                            setIsSortDropdownOpen(false);
                          }}
                        >
                          {UNSELECTED_SORT_OPTIONS.map((sortOption) => (
                            <div
                              className="flex text-black pl-10 h-50 items-center hover:bg-disabled"
                              key={sortOption.key}
                              onClick={() => {
                                sortOption.onClick();
                                setIsSortDropdownOpen(false);
                              }}
                              font-customisation="para-text"
                              color-customisation="page-body-text"
                            >
                              {sortOption.unSelectedName ?? sortOption.name}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
          {categoryProductsSlice?.error ? (
            <div className="text-center text-bold border rounded m-100 p-25">{categoryProductsSlice?.error}</div>
          ) : null}
          <div className="w-full mt-40">
            {categoryProductsSlice?.isFetching ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3">
                <ProductCardLoading />
                <ProductCardLoading />
                <ProductCardLoading />
              </div>
            ) : (
              <>
                {categoryProductsSlice?.list?.results?.length ? (
                  <div className={classnames('flex flex-wrap h-full justify-evenly md:justify-start gap-20 md:gap-30')}>
                    {categoryProductsSlice?.list?.results?.map((product, index: number) => (
                      <div className={classnames('min-w-182 max-w-182 md:min-w-260 md:max-w-260 md:mb-30')} key={index}>
                        <ProductCard
                          addToast={(...args) => dispatch(addToast(...args))}
                          product={product}
                          addProductToCart={(...args) => dispatch(addProductToCart(...args))}
                          referredFrom={PRODUCT_REFERRED_FROM.CATEGORY}
                          subtractProductFromCart={(...args) => dispatch(subtractProductFromCart(...args))}
                          cartProductQuantity={cart.quantityByProductId[product.id] || 0}
                          storeInfo={storeInfo}
                          dimension={'h-182 w-182 md:w-260 md:h-260'}
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <>
                    <div className="justify-center text-14 text-center text-theme md:mt-150 mt-100">
                      <img className="m-auto h-100 mb-20" src={CartImg} alt="Empty cart" />
                      <div
                        className="my-14 text-20 font-medium text-gray"
                        font-customisation="section-heading"
                        color-customisation="page-heading"
                      >
                        Coming Soon&nbsp;!
                      </div>
                    </div>
                  </>
                )}
              </>
            )}
          </div>
          <div className="h-100">
            <Pagination
              className="absolute text-12 bottom-0"
              hideJumpToSection
              {...categoryProductsSlice?.pagination}
              onClick={(pageNumber) => {
                dispatch(resetProductsStore(PRODUCTS_KEY.CATEGORY));
                dispatch(
                  fetchProductsRequest({
                    storeKey: PRODUCTS_KEY.CATEGORY,
                    queryParams: {
                      ...categoryProductsSlice?.currentQueryParams,
                      page: pageNumber,
                    },
                  }),
                );
              }}
            />
          </div>
        </div>
      </div>
    </PageLayout>
  );
};

// eslint-disable-next-line
export default () => {
  return (
    <CategoryProvider>
      <Category />
    </CategoryProvider>
  );
};
